import { useEffect, useId, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Mail, MapPin, Menu, Phone, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth';
import { homePathFor } from '@/lib/home-path';

/** Organisation details come from settings, not from constants in the markup. */
export interface PublicSettings {
  'org.name'?: string;
  'org.legal_name'?: string;
  'org.email'?: string;
  'org.phone'?: string;
  'org.address'?: string;
}

export const FALLBACK_ORG: Required<PublicSettings> = {
  'org.name': 'AAF-UNIFEMGA',
  'org.legal_name':
    "Aliu Abdulkabir Foundation, in partnership with the Obafemi Awolowo University Muslim Graduates' Association",
  'org.email': 'nunifemga@gmail.com',
  'org.phone': '+2349168484980',
  'org.address': 'Central Mosque, OAU, Ile-Ife, Osun State',
};

export function usePublicSettings() {
  const { data } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings/public'),
    staleTime: 5 * 60 * 1000,
  });

  return { ...FALLBACK_ORG, ...(data ?? {}) };
}

/** Formats +2349168484980 as (+234) 916 848 4980. */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('234')) {
    const local = digits.slice(3);
    return `(+234) ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  return raw;
}

const NAV = [
  { to: '/loans', label: 'Loans' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQs' },
  { to: '/contact', label: 'Contact' },
];

/**
 * Shell for every page a member sees before signing in.
 *
 * One header and one footer across the marketing site, so the pages cannot
 * drift into looking like different organisations — and so the mobile drawer
 * only has to be right once.
 */
export function PublicShell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  // Lock the page behind the drawer; a scrolling background under an open menu
  // is the classic mobile-nav bug.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/85 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between gap-4 md:h-20">
          <Link to="/" className="wordmark shrink-0 text-xl text-foreground sm:text-2xl">
            AAF-UNIFEMGA
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'text-sm font-bold uppercase tracking-wider transition-colors hover:text-primary',
                    isActive ? 'text-primary' : 'text-foreground/70',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {user ? (
              <Link
                to={homePathFor(user)}
                className="hidden rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-brand transition-all hover:-translate-y-0.5 sm:inline-flex sm:items-center sm:gap-1.5"
              >
                {user.isAdmin ? 'Go to dashboard' : 'Go to my portal'}
                <ArrowRight size={15} />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden rounded-full px-4 py-2 text-sm font-bold text-foreground/70 transition-colors hover:text-primary sm:block"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="hidden rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-brand transition-all hover:-translate-y-0.5 sm:inline-block"
                >
                  Create account
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="-mr-1 rounded-lg p-2.5 text-foreground transition-colors hover:bg-accent lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 right-0 flex w-[min(320px,85vw)] flex-col bg-background shadow-floating">
            <div className="flex h-16 items-center justify-between border-b border-border px-5">
              <span className="wordmark text-xl text-foreground">AAF-UNIFEMGA</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-mr-1 rounded-lg p-2.5 text-foreground transition-colors hover:bg-accent"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4">
              {NAV.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center justify-between rounded-xl px-4 py-3.5 text-base font-bold transition-colors',
                      isActive
                        ? 'bg-primary-soft text-primary-soft-foreground'
                        : 'text-foreground hover:bg-accent',
                    )
                  }
                >
                  {label}
                  <ArrowRight size={16} className="opacity-40" />
                </NavLink>
              ))}
            </nav>

            <div className="space-y-2 border-t border-border p-4">
              {user ? (
                <Link
                  to={homePathFor(user)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 font-bold text-primary-foreground shadow-brand"
                >
                  {user.isAdmin ? 'Go to dashboard' : 'Go to my portal'}
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 font-bold text-primary-foreground shadow-brand"
                  >
                    Create account <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/login"
                    className="flex w-full items-center justify-center rounded-xl border border-border px-5 py-3.5 font-bold text-foreground"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  const org = usePublicSettings();

  return (
    <footer className="relative overflow-hidden bg-foreground text-background">
      {/* The pattern is the same one used in the hero, at low contrast. */}
      <GeometricPattern className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 text-background/[0.04]" />

      <div className="container relative py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h3 className="wordmark mb-4 text-3xl md:text-4xl">AAF-UNIFEMGA</h3>
            <p className="max-w-sm text-sm font-medium leading-relaxed text-background/60">
              {org['org.legal_name']} — interest-free member lending, built on trust.
            </p>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-background/15 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-background/70">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Qard Hasan · 0% forever
            </p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="mb-5 font-heading text-xs font-black uppercase tracking-[0.2em] text-primary">
              Explore
            </h4>
            <ul className="space-y-2.5 text-sm font-medium text-background/70">
              {[
                { to: '/loans', label: 'Loan products' },
                { to: '/about', label: 'About us' },
                { to: '/faq', label: 'FAQs' },
                { to: '/contact', label: 'Contact us' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="inline-block py-1 transition-colors hover:text-primary">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="mb-5 font-heading text-xs font-black uppercase tracking-[0.2em] text-primary">
              Reach us
            </h4>
            <ul className="space-y-3 text-sm font-medium text-background/70">
              <li className="flex gap-2.5">
                <MapPin size={15} className="mt-0.5 shrink-0 text-background/40" />
                {org['org.address']}
              </li>
              <li className="flex gap-2.5">
                <Phone size={15} className="mt-0.5 shrink-0 text-background/40" />
                <a
                  href={`tel:${org['org.phone']}`}
                  className="numeric transition-colors hover:text-primary"
                >
                  {formatPhone(org['org.phone'])}
                </a>
              </li>
              <li className="flex gap-2.5">
                <Mail size={15} className="mt-0.5 shrink-0 text-background/40" />
                <a
                  href={`mailto:${org['org.email']}`}
                  className="break-all transition-colors hover:text-primary"
                >
                  {org['org.email']}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-background/10 pt-8 text-sm text-background/40 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {org['org.name']}. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy" className="transition-colors hover:text-primary">
              Privacy policy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-primary">
              Terms and conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * An eight-fold rosette, the geometry that runs through Islamic ornament.
 *
 * Drawn rather than fetched: the artifact CSP aside, a decorative background
 * should not cost a network request, and this scales to any size without a
 * second asset.
 */
export function GeometricPattern({ className }: { className?: string }) {
  // Several of these render on one page, so the pattern id has to be unique
  // per instance or the document carries duplicate ids.
  const id = `rosette-${useId().replace(/:/g, '')}`;

  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
      <defs>
        <pattern id={id} width="50" height="50" patternUnits="userSpaceOnUse">
          <g fill="none" stroke="currentColor" strokeWidth="1">
            <circle cx="25" cy="25" r="18" />
            <rect x="7" y="7" width="36" height="36" />
            <rect x="7" y="7" width="36" height="36" transform="rotate(45 25 25)" />
            <path d="M25 2 L48 25 L25 48 L2 25 Z" />
          </g>
        </pattern>
      </defs>
      <rect width="200" height="200" fill={`url(#${id})`} />
    </svg>
  );
}
